import { useMemo, useState } from "react";
import {
  getCoreRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type Getter,
  type Row,
  type TableOptions,
} from "@tanstack/react-table";
import type { QueryKey } from "@tanstack/react-query";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import type { GetPagedDataViewModel } from "../../types/viewModels/dataViews";
import type {
  ColumnKey,
  Entity,
  TableGridProps,
  TableStateProps,
  UpdateEntityItem,
} from "@repo/types/ui/tables";
import { dataViewKeys } from "@repo/types/queries";
import { renderGridTemplateColumns } from "../../helpers/tableHelper";
import { useApiAccess } from "@repo/hooks/contexts";
import { useGetDataViewEntities } from "../../hooks/queries";
import { TableHeaderCell, TableDataCell, TableSkeleton } from "../tables";
// import { CheckboxSelection } from "../../../checkboxSelection/checkboxSelection";
import { EditableText } from "../ui/textEditor/EditableText";
import CheckboxSelection from "../checkboxSelection/CheckboxSelection";

export interface TableProps<T extends Entity>
  extends TableGridProps<T>, TableStateProps {
  columns: ColumnDef<T>[];
  hasGroup?: boolean;
  getPagedDataViewModel: GetPagedDataViewModel<T>;
}

export function Table<T extends Entity>({
  columns,
  getPagedDataViewModel,
  entityType,
  onCellValueUpdated,
  sorting,
  hasGroup,
  onSortingChange,
  columnOrder,
  onColumnOrderChange,
  columnSizing,
  onColumnSizingChange,
  columnPinning,
  onColumnPinningChange,
}: TableProps<T>) {
  const { apiClient } = useApiAccess();

  const queryKey: QueryKey = useMemo(
    () => dataViewKeys.listByRelationId(getPagedDataViewModel),
    [getPagedDataViewModel],
  );

  const { data: pagedData, isPending: isPagedDataLoading } =
    useGetDataViewEntities<T>(queryKey, apiClient, getPagedDataViewModel);

  const flatData = useMemo(
    () => pagedData?.pages?.flatMap((page) => page.Data) ?? [],
    [pagedData],
  );

  const [rowSelection, setRowSelection] = useState({});

  const defaultColumn: Partial<ColumnDef<T>> = {
    cell: ({
      getValue,
      row,
      column,
    }: {
      getValue: Getter<unknown>;
      row: Row<T>;
      column: Column<T>;
    }) => (
      <EditableText
        value={getValue() as string}
        onCommit={(newValue) => {
          const updateItem: UpdateEntityItem<T> = {
            RecordId: row.original.Id,
            ColumnId: column.id as ColumnKey<T>,
            Value: newValue as T[ColumnKey<T>],
            EntityType: entityType,
          };
          onCellValueUpdated(updateItem, [queryKey]);
        }}
      />
    ),
  };

  const tableOptions: TableOptions<T> = {
    columns: columns,
    data: flatData ?? [],
    defaultColumn: defaultColumn,
    columnResizeMode: "onChange",
    state: {
      sorting: sorting,
      columnOrder: columnOrder,
      columnSizing: columnSizing,
      columnPinning: columnPinning,
      rowSelection: rowSelection, // ADDED FOR CHECKBOX
    },
    enableRowSelection: true, // ADDED FOR CHECKBOX
    onRowSelectionChange: setRowSelection, // ADDED FOR CHECKBOX
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: onSortingChange,
    onColumnOrderChange: onColumnOrderChange,
    onColumnSizingChange: onColumnSizingChange,
    onColumnPinningChange: onColumnPinningChange,
  };

  const table = useReactTable(tableOptions);
  const selectedRows = table.getSelectedRowModel().rows;

  const headerGroup = table.getHeaderGroups()[0];
  const orderedColumns = table.getVisibleLeafColumns();
  const { rows } = table.getRowModel();

  // reorder columns after drag & drop
  function handleColumnOrderChange(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      onColumnOrderChange((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
        return ["select", ...newOrder.filter((id) => id !== "select")]; //this is just a splice util
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const tableSize = orderedColumns.reduce(
    (total, col) => total + col.getSize(),
    0,
  );

  if (!pagedData && isPagedDataLoading) {
    return <TableSkeleton rowCount={10} columnDefs={columns} />;
  }

  return (
    <>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleColumnOrderChange}
        sensors={sensors}
      >
        <div
          role="table"
          className="rounded bg-white shadow-sm"
          style={{ width: `${tableSize}px` }}
        >
          <div
            className={`grid w-full auto-rows-[35px] sticky text-sm bg-white z-50
            ${hasGroup ? "top-9" : "top-0"}
          `}
            style={{
              ...renderGridTemplateColumns(headerGroup.headers),
            }}
          >
            <SortableContext
              items={columnOrder}
              strategy={horizontalListSortingStrategy}
            >
              {orderedColumns.map((column) => {
                const header = headerGroup.headers.find(
                  (h) => h.column.id === column.id,
                )!;

                return <TableHeaderCell key={header.id} header={header} />;
              })}
            </SortableContext>
          </div>

          {rows.map((row: Row<T>) => (
            <div
              key={row.id}
              className={`grid w-full auto-rows-[35px] text-sm hover:bg-gray-100 hover:shadow-[0_1px_2px_rgba(0,0,0,0.12)] hover:z-100 cursor-pointer`}
              style={{
                ...renderGridTemplateColumns(headerGroup.headers),
              }}
            >
              {orderedColumns.map((column) => {
                const cell = row
                  .getAllCells()
                  .find((c) => c.column.id === column.id)!;

                return <TableDataCell key={cell.id} cell={cell} />;
              })}
            </div>
          ))}
        </div>
      </DndContext>

      <CheckboxSelection
        count={selectedRows.length}
        onClear={() => table.resetRowSelection()}
      />
    </>
  );
}
